import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Button } from "react-bootstrap";
import LiquidVariablesModal from "./LiquidVariablesModal";
import TemplateFieldList from "./TemplateFieldList";
import { Event, LiquidVariables } from "../../../models/Event";
import { TemplateFile } from "../../../models/Templates";
import { QrCodeSpec } from "../../../models/TemplateMeta";
import { FieldMappings, FieldValidation } from "./useCreateWizard";
import { BaseValidatedFormGroup } from "./validation";
import { mapLiquidVariables } from "../../../utils/liquidUtils";

interface StepMapFieldsProps {
    template: TemplateFile | null;
    event: Event | null;
    liquidVariables: LiquidVariables | null;
    fieldMappings: FieldMappings;
    onFieldMappingsChange: (mappings: FieldMappings) => void;
    qrCodes: QrCodeSpec[];
    qrCodeOverrides: Record<string, string> | null;
    onQrCodeOverridesChange: (overrides: Record<string, string> | null) => void;
    onQrFocus?: (qrId: string) => void;
    onQrHover?: (qrId: string | null) => void;
    onFieldFocus?: (fieldName: string) => void;
    onFieldHover?: (fieldName: string | null) => void;
    validation?: FieldValidation;
}

const StepMapFields: React.FC<StepMapFieldsProps> = ({
    template,
    event,
    liquidVariables,
    fieldMappings,
    onFieldMappingsChange,
    qrCodes,
    qrCodeOverrides,
    onQrCodeOverridesChange,
    onQrFocus,
    onQrHover,
    onFieldFocus,
    onFieldHover,
    validation,
}) => {
    const { t } = useTranslation(["wizard", "liquid"]);
    const [showVariablesModal, setShowVariablesModal] = useState(false);
    const mappedVariables = mapLiquidVariables(liquidVariables ?? {}, event, t);
    return (
        <div className="step-map-fields">
            <h3 className="mb-4">{t("mapFields.stepTitle")}</h3>
            <p className="text-muted mb-4">
                {t("mapFields.descriptionStart")}
                <Button variant="link" className="p-0 align-baseline" onClick={() => setShowVariablesModal(true)}>
                    {t("mapFields.descriptionLink")}
                </Button>
                {t("mapFields.descriptionEnd")}
            </p>
            <LiquidVariablesModal
                show={showVariablesModal}
                onClose={() => setShowVariablesModal(false)}
                liquidVariables={mappedVariables}
            />

            {template && event ? (
                <Row>
                    <Col>
                        <BaseValidatedFormGroup
                            validation={validation}
                            className="form-group"
                            controlId="fieldMappings"
                        >
                            <TemplateFieldList
                                template={template}
                                event={event}
                                liquidVariables={liquidVariables}
                                fieldMappings={fieldMappings}
                                onFieldMappingsChange={onFieldMappingsChange}
                                qrCodes={qrCodes}
                                qrCodeOverrides={qrCodeOverrides}
                                onQrCodeOverridesChange={onQrCodeOverridesChange}
                                onQrFocus={onQrFocus}
                                onQrHover={onQrHover}
                                onFieldFocus={onFieldFocus}
                                onFieldHover={onFieldHover}
                            />
                        </BaseValidatedFormGroup>
                    </Col>
                </Row>
            ) : (
                <div className="alert alert-warning">
                    <p className="mb-0">{t("mapFields.warning")}</p>
                </div>
            )}
        </div>
    );
};

export default StepMapFields;
